import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import dayjs from 'dayjs';

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ChartHeader from 'components/charts/ChartHeader';
import { DataCard } from 'components/charts/DataCards';
import BaseChart, { CHART_TYPES } from 'components/charts/chartJS/BaseChart';
import { DataCardsWrapper } from 'components/charts/styled/DataCards.styled';
import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import Popper from 'components/global/Popper';
import Typography from 'components/global/Typography';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';
import DynamicListStringInput from 'components/list/FilterInputs/DynamicListStringInput';

import { AUTOCOMPLETE, ENUMS } from 'services/consts';

import { colorCombinations } from 'utils/colors';
import PATHS, { mergePaths } from 'utils/paths';

import * as S from './AggregatorAging.styled';
import { getAnalysisData, getDCAnalysisData } from './AggregatorAgingSlice';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const FILTERS = {
	shipmentCreatedDate: 'shipmentCreatedDate',
	originCountry: 'originCountry',
	originCity: 'originCity',
	serviceType: 'serviceType',
	contactName: 'contactName',
	clientName: 'clientName',
	destinationCity: 'destinationCity',
	maxTier: 'maxTier',
	deliveryCompanyName: 'deliveryCompanyName',
	destinationCountry: 'destinationCountry',
	shipmentFlow: 'shipmentFlow',
	delayType: 'delayType',
	dcAccountCountry: 'dcAccountCountry'
};

const FILTER_LIST = {
	[FILTERS.shipmentCreatedDate]: {
		key: [FILTERS.shipmentCreatedDate],
		action: FilterAction.between,
		filterType: FilterType.date,
		from: dayjs().subtract(90, 'day').endOf('day').toISOString(),
		to: dayjs().endOf('day').toISOString()
	},
	[FILTERS.originCountry]: {
		key: [FILTERS.originCountry],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.originCity]: {
		key: [FILTERS.originCity],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.serviceType]: {
		key: [FILTERS.serviceType],
		action: FilterAction.is,
		filterType: FilterType.multiSelection,
		value: []
	},
	[FILTERS.contactName]: {
		key: [FILTERS.contactName],
		action: FilterAction.is,
		filterType: FilterType.string,
		value: []
	},
	[FILTERS.clientName]: {
		key: [FILTERS.clientName],
		action: FilterAction.is,
		filterType: FilterType.enumMulti,
		value: []
	},
	[FILTERS.destinationCity]: {
		key: [FILTERS.destinationCity],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.maxTier]: {
		key: [FILTERS.maxTier],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.deliveryCompanyName]: {
		key: [FILTERS.deliveryCompanyName],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.destinationCountry]: {
		key: [FILTERS.destinationCountry],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.shipmentFlow]: {
		key: [FILTERS.shipmentFlow],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.delayType]: {
		key: [FILTERS.delayType],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	},
	[FILTERS.dcAccountCountry]: {
		key: [FILTERS.dcAccountCountry],
		action: FilterAction.is,
		filterType: FilterType.enum,
		value: []
	}
};

const AggregatorAgingOverview = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const analysisData = useSelector((state) => state.aggregatorAging.analysis);
	const dcAnalysisData = useSelector((state) => state.aggregatorAging.dcAnalysis);
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [dcFiltersMenuButtonRef, setDcFilterMenuButtonRef] = useState(null);
	const [filters, setFilter] = useState(JSON.parse(JSON.stringify(FILTER_LIST)));
	const [dcFilters, setDcFilter] = useState({});

	useEffect(() => {
		const filterList = [];
		Object.keys(filters).forEach((key) => {
			const filterObj = filters[key];
			if (filterObj?.value?.length === 0 && !filterObj?.from) {
				return;
			}
			filterList.push(filterObj);
		});
		dispatch(getAnalysisData(filterList));
	}, [filters]);

	useEffect(() => {
		const filterList = Object.keys(dcFilters).map((key) => dcFilters[key]);
		dispatch(getDCAnalysisData(filterList));
	}, [dcFilters]);

	const chartOptions = {
		borderRadius: 2,
		barThickness: 20,
		barPercentage: 0.1,
		plugins: {
			legend: {
				display: false
			}
		},
		scales: {
			x: {
				title: {
					display: true,
					text: t('agingnumberOfDays'),
					padding: {
						top: 5,
						bottom: 5
					}
				}
			},
			y: {
				title: {
					display: true,
					text: t('numberOfOrders')
				}
			}
		}
	};

	const dcChartOptions = {
		layout: {
			height: 100
		},
		borderRadius: 2,
		categoryPercentage: 1,
		plugins: {
			legend: {
				display: false
			}
		},
		responsive: true,
		maintainAspectRatio: false,
		scales: {
			x: {
				title: {
					display: true,
					text: t('deliveryCompanyName'),
					padding: {
						top: 30,
						bottom: 30
					}
				}
			},
			y: {
				title: {
					display: true,
					text: t('numberOfOrders')
				}
			}
		}
	};

	const handleNumberCard = (key) => {
		const filter = {
			accessor: 'delayType',
			action: FilterAction.is,
			value: []
		};
		switch (key) {
			case 'pickupAging':
				filter.value = ['pickupDelay', 'reversePickupDelay'];
				break;
			case 'deliveryAging':
				filter.value = ['deliveryDelay', 'reverseDeliveryDelay'];
				break;
			case 'totalAging':
				filter.value = ['pickupDelay', 'reversePickupDelay', 'deliveryDelay', 'reverseDeliveryDelay'];
				break;
			default:
				break;
		}
		navigate(mergePaths(PATHS.AGGREGATOR_AGING, PATHS.AGGREGATOR_AGING_SHIPMENTS), {
			state: {
				cardFilter: filter,
				analysisFilters: Object.keys(filters).map((key) => ({
					accessor: key,
					action: FilterAction.is,
					value: filters[key].value
				}))
			}
		});
	};

	const NUMBERS_CARDS_CONFIG = [
		{
			accessor: 'totalPickupCount',
			label: 'pickupAging',
			customIcon: 'fa-solid fa-truck-fast',
			backgroundColor: 'var(--fa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)',
			onButtonClick: () => handleNumberCard('pickupAging')
		},
		{
			accessor: 'totalDeliveryCount',
			label: 'deliveryAging',
			customIcon: 'fa-solid fa-truck-fast',
			backgroundColor: 'var(--fa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)',
			onButtonClick: () => handleNumberCard('deliveryAging')
		},
		{
			accessor: 'totalCount',
			label: 'totalAging',
			customIcon: 'fa-solid fa-truck-fast',
			backgroundColor: 'var(--fa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)',
			onButtonClick: () => handleNumberCard('totalAging')
		}
	];

	const sortByCount = useCallback((itemList, prefix) => {
		const sortedItems = itemList.sort((a, b) => parseInt(a.replace(prefix, '')) - parseInt(b.replace(prefix, '')));

		const dayItems = sortedItems.filter((item) => item.includes('Day'));
		const weekItems = sortedItems.filter((item) => item.includes('Week'));

		return dayItems.concat(weekItems);
	});

	const prepareChartData = (data, prefix) => {
		if (analysisData.isLoading) {
			return {
				labels: [t('loading')],
				datasets: [{ data: [0] }]
			};
		}

		try {
			const labels = sortByCount(Object.keys(data), prefix);
			const datasets = [
				{
					data: labels.map((item) => data[item]),
					backgroundColor: labels.map(
						(_, index) =>
							colorCombinations[
								index < 2
									? colorCombinations.length - 1
									: index < 8
										? colorCombinations.length - 2
										: colorCombinations.length - 3
							].color
					)
				}
			];
			return { labels, datasets };
		} catch {
			return {
				labels: [],
				datasets: [{ data: [0] }]
			};
		}
	};

	const prepareDCChartData = (data) => {
		if (dcAnalysisData.isLoading) {
			return {
				labels: [t('loading')],
				datasets: [{ data: [0] }]
			};
		}

		try {
			const groupData = [...Array(3)].map((_, idx) =>
				data.map((item) => item[idx === 0 ? 'greenCount' : idx === 1 ? 'orangeCount' : 'redCount'])
			);
			const labels = data.map((item) => item.deliveryCompanyName ?? '-');
			const datasets = [...Array(3)].map((_, idx) => ({
				data: groupData[idx],
				backgroundColor: data.map(() => colorCombinations[15 - idx].color),
				categoryPercentage: 0.7,
				barPercentage: 0.7
			}));
			return { labels, datasets };
		} catch {
			return {
				labels: [],
				datasets: [{ data: [0] }]
			};
		}
	};

	const changeFilter = (key, value, filterType = 'analysis', type = 'string') => {
		const filterFunc = filterType === 'analysis' ? setFilter : setDcFilter;

		// delete filter if value is null or empty
		if (value === null || (type === 'date' && !value.from && !value.to)) {
			filterFunc((prev) => {
				const newFilter = { ...prev };
				delete newFilter[key];
				return newFilter;
			});
			return;
		}
		filterFunc((prev) => ({
			...prev,
			[key]: {
				...FILTER_LIST[key],
				...prev[key],
				...(type === 'string' ? { value } : { to: value.to, from: value.from })
			}
		}));
	};

	return (
		<S.Container className="pt-4 pb-5">
			<div>
				<ChartHeader
					title={' '}
					filter={
						<>
							<Button
								id="filters-button"
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
								ref={setFilterMenuButtonRef}
								variant="borderless"
								label="allFilters"
							/>
							{filtersMenuButtonRef && (
								<Popper refElement={filtersMenuButtonRef} placement="bottom-end">
									{() => (
										<S.PopperMenu>
											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													shipmentCreatedDate
												</Typography>
												<DateInput
													value={{
														start: filters?.[FILTERS.shipmentCreatedDate]?.from,
														end: filters?.[FILTERS.shipmentCreatedDate]?.to
													}}
													setValue={(date) =>
														changeFilter(
															FILTERS.shipmentCreatedDate,
															{ from: date?.start, to: date?.end },
															'analysis',
															'date'
														)
													}
													rangeMode
												/>
											</S.PopperMenuItem>
											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													originCountry
												</Typography>
												<EnumSelect
													enumName={ENUMS.COUNTRIES}
													value={filters[FILTERS.originCountry]?.value}
													setValue={(value) =>
														changeFilter(FILTERS.originCountry, value ? [value] : null)
													}
													labelPrefix="country."
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													originCity
												</Typography>
												<AutoComplete
													autoCompleteName={AUTOCOMPLETE.CITIES_LIST}
													setValue={(value) => {
														changeFilter(
															FILTERS.originCity,
															value?.name ? [value.name] : null
														);
													}}
													selectionLabelAccessor={['name', 'country']}
													columns={[
														{ accessor: 'name', width: 200 },
														{ accessor: 'country', width: 100 }
													]}
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													serviceType
												</Typography>
												<EnumSelect
													enumName={ENUMS.PRICE_CONFIGURATION_SERVICE_TYPE}
													value={filters[FILTERS.serviceType]?.value || []}
													multipleSelection
													setValue={(value) =>
														changeFilter(FILTERS.serviceType, value ? value : null)
													}
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													contactName
												</Typography>
												<DynamicListStringInput
													value={filters[FILTERS.contactName]?.value}
													setValue={(value) =>
														changeFilter(FILTERS.contactName, value ? [value] : null)
													}
													autoFocus={false}
													style={{ padding: 0 }}
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													clientName
												</Typography>
												<AutoComplete
													autoCompleteName={AUTOCOMPLETE.CLIENTS}
													value={filters[FILTERS.clientName]?.value}
													setValue={(value) => {
														changeFilter(
															FILTERS.clientName,
															value ? value.map((item) => item.ID) : null
														);
													}}
													columns={[
														{ accessor: 'ID', width: 100 },
														{ accessor: 'Name', width: 400 }
													]}
													selectionLabelAccessor={['ID', 'Name']}
													multipleSelection
													fixedContainerWidth={false}
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													destinationCity
												</Typography>
												<AutoComplete
													autoCompleteName={AUTOCOMPLETE.CITIES_LIST}
													setValue={(value) => {
														changeFilter(
															FILTERS.destinationCity,
															value?.name ? [value.name] : null
														);
													}}
													selectionLabelAccessor={['name', 'country']}
													columns={[
														{ accessor: 'name', width: 200 },
														{ accessor: 'country', width: 100 }
													]}
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													maxTier
												</Typography>
												<EnumSelect
													enumName={ENUMS.DC_CITY_TIER}
													value={filters[FILTERS.maxTier]?.value}
													setValue={(value) =>
														changeFilter(FILTERS.maxTier, value ? [value] : null)
													}
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													deliveryCompanyName
												</Typography>
												<EnumSelect
													enumName={ENUMS.DELIVERY_COMPANY_SETTINGS_FOR_AGGREGATORS_WITH_OMNI}
													value={filters[FILTERS.deliveryCompanyName]?.value}
													setValue={(value) =>
														changeFilter(
															FILTERS.deliveryCompanyName,
															value ? [value] : null
														)
													}
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													destinationCountry
												</Typography>
												<EnumSelect
													enumName={ENUMS.COUNTRIES}
													value={filters[FILTERS.destinationCountry]?.value}
													setValue={(value) =>
														changeFilter(FILTERS.destinationCountry, value ? [value] : null)
													}
													labelPrefix="country."
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													shipmentFlow
												</Typography>
												<EnumSelect
													enumName={ENUMS.SHIPMENT_FLOW}
													value={filters[FILTERS.shipmentFlow]?.value}
													setValue={(value) =>
														changeFilter(FILTERS.shipmentFlow, value ? [value] : null)
													}
												/>
											</S.PopperMenuItem>
										</S.PopperMenu>
									)}
								</Popper>
							)}
						</>
					}
				/>
				<DataCardsWrapper>
					{NUMBERS_CARDS_CONFIG.map((config) => (
						<DataCard
							key={config.accessor}
							loading={analysisData.isLoading}
							cardConfig={config}
							data={analysisData.data?.totalNumbers?.[config.accessor] ?? '-'}
						/>
					))}
				</DataCardsWrapper>
			</div>
			<S.ChartContainer>
				<S.BarChartContainer>
					<ChartHeader title="pickupAging" />
					<BaseChart
						type={CHART_TYPES.bar}
						options={chartOptions}
						data={prepareChartData(analysisData.data?.pickupCounts, 'count')}
					/>
				</S.BarChartContainer>
				<S.BarChartContainer>
					<ChartHeader title="deliveryAging" />
					<BaseChart
						type={CHART_TYPES.bar}
						options={chartOptions}
						data={prepareChartData(analysisData.data?.deliveryCounts, 'count')}
					/>
				</S.BarChartContainer>
				<S.BarChartContainer>
					<ChartHeader title="totalAging" />
					<BaseChart
						type={CHART_TYPES.bar}
						options={chartOptions}
						data={prepareChartData(analysisData.data?.totalCounts, 'totalCount')}
					/>
				</S.BarChartContainer>
			</S.ChartContainer>
			<S.DCBarChartContainer>
				<ChartHeader
					title="totalAgingByDC"
					filter={
						<>
							<Button
								id="filters-button"
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
								ref={setDcFilterMenuButtonRef}
								variant="borderless"
								label="allFilters"
							/>
							{dcFiltersMenuButtonRef && (
								<Popper refElement={dcFiltersMenuButtonRef} placement="bottom-end">
									{() => (
										<S.PopperMenu>
											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													contactName
												</Typography>
												<DynamicListStringInput
													value={dcFilters[FILTERS.contactName]?.value}
													setValue={(value) =>
														changeFilter(FILTERS.contactName, value ? [value] : null, 'dc')
													}
													autoFocus={false}
													style={{ padding: 0 }}
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													delayType
												</Typography>
												<EnumSelect
													enumName={ENUMS.DELAY_TYPE}
													value={dcFilters[FILTERS.delayType]?.value || []}
													setValue={(value) =>
														changeFilter(FILTERS.delayType, value ? value : null, 'dc')
													}
													multipleSelection
												/>
											</S.PopperMenuItem>

											<S.PopperMenuItem>
												<Typography variant="p2" fontWeight="500">
													dcAccountCountry
												</Typography>
												<EnumSelect
													enumName={ENUMS.DC_COUNTRIES}
													value={dcFilters[FILTERS.dcAccountCountry]?.value}
													setValue={(value) =>
														changeFilter(
															FILTERS.dcAccountCountry,
															value ? [value] : null,
															'dc'
														)
													}
												/>
											</S.PopperMenuItem>
										</S.PopperMenu>
									)}
								</Popper>
							)}
						</>
					}
				/>
				<BaseChart
					type={CHART_TYPES.bar}
					options={dcChartOptions}
					data={prepareDCChartData(dcAnalysisData.data)}
				/>
			</S.DCBarChartContainer>
		</S.Container>
	);
};

export default AggregatorAgingOverview;
